<template>
  <QDialog v-model="showDialog" no-esc-dismiss persistent>
    <QCard v-if="initGBGIdScan" style="padding: 0 0 25px 0; width: 700px; max-width: 80vw">
      <QCardSection class="row justify-end q-pb-none">
        <QBtn flat outline round color="grey-9" @click="onCancel" :icon="close" />
      </QCardSection>
      <IDScan :finished="finished" :initialised="initialised" :started="started" :error="error" :icons="icons" @start="startFlow" />
      <UploadDialogBox v-if="manualUpload" @onSaved="onSaved" @onClosed="onCancel" />
    </QCard>
    <QCard v-else style="padding: 0 0 25px 0; width: 700px; max-width: 80vw">
      <div class="col-xs-12 col-sm-10 col-md-8" style="text-align: center; padding: 100px 0">
        <QSpinner color="primary" size="3em" />
      </div>
    </QCard>
  </QDialog>
</template>

<script>
import { mapGetters, mapState, mapMutations, mapActions } from 'vuex';
import { QBtn, QDialog, QCard, QCardSection, QSpinner } from 'quasar';
import MutateCreateOrUpdateProperty from '../../graphql/mutations/MutateCreateOrUpdateProperty';
import MutateGetSignedUrl from '../../graphql/mutations/MutateGetSignedUrl';
import QueryIDScan from './../../graphql/queries/QueryIDScan';
import { GLOBAL_ERROR } from '../../components/GlobalError/GlobalError';
import { GlobalDialog } from '../../components/Dialogs/GlobalDialog';
import IDScan from '../../utils/id-scan';
import { isHighLevelResultPassed } from '../../utils/id-scan/high-level-result';
import mapRetrieval from '../../utils/id-scan/map-retrieval';
import { Icons } from 'eclipx.ui';
import appConfig from '../../utils/app-config';
import UploadDialogBox from './UploadDocuments/UploadDocuments.vue';

export default {
  name: 'IDScanDialog',
  props: {},
  data() {
    return {
      error: false,
      initialised: false,
      started: false,
      finished: false,
      IDScan: undefined,
      displayingError: false,
      highLevelResult: null,
      icons: {},
      token: null,
      fileUrls: [],
      showDialog: true,
      errorCount: 0,
      manualUpload: false
    };
  },
  created() {
    this.close = Icons.evaClose;
    this.icons = {
      selfie: '/assets/images/DriversLicence_image.png',
      photoId: '/assets/images/Selfie_image.png'
    };
    this.errorCount = this.getErrorCount(this.isUserPrimaryApplicant ? 'primaryApplicant' : 'secondaryApplicant') || 0;
  },
  computed: {
    ...mapGetters({
      getPrimaryApplicantIDScanData: 'vehicleApplication/getPrimaryApplicantIDScanData',
      getSecondaryApplicantIDScanData: 'vehicleApplication/getSecondaryApplicantIDScanData',
      isUserPrimaryApplicant: 'vehicleApplication/isUserPrimaryApplicant',
      getErrorCount: 'vehicleApplication/errorCounts'
    }),
    personalDetails() {
      if (this.isUserPrimaryApplicant) return this.getPrimaryApplicantIDScanData;
      else return this.getSecondaryApplicantIDScanData;
    }
  },
  methods: {
    ...mapMutations({
      setIDScanDataStatus: 'vehicleApplication/setIDScanDataStatus'
    }),
    onCancel: async function (value = null) {
      this.showDialog = false;
      this.$emit('close', value);
    },
    async updateIDScanData(dataParam) {
      return await this.$apollo
        .mutate({
          mutation: MutateCreateOrUpdateProperty,
          variables: {
            applicationId: this.$store.getters['vehicleApplication/vehicleApplication']
              ? this.$store.getters['vehicleApplication/vehicleApplication'].applicationId
              : null,
            params: {
              value: 'idScanData',
              idScanData: dataParam,
              propertyFirst: this.isUserPrimaryApplicant ? 'primaryApplicant' : 'secondaryApplicant',
              propertySecond: 'idScanData'
            }
          }
        })
        .catch((error) => {
          let gqlError = error.graphQLErrors[0];
          this.error = true;
          this.showDialog = false;
          if (error) {
            this.$euiNotifications.emit({
              notification: GLOBAL_ERROR
            });
          }
        });
    },
    async startFlow() {
      if (this.errorCount > 0) {
        this.manualUpload = true;
        return;
      }
      this.started = true;
      const env = {
        VUE_APP_ID_SCAN_BACKEND_URL: appConfig.VUE_APP_IDSCAN_ROOT_URL ? appConfig.VUE_APP_IDSCAN_ROOT_URL : 'https://sydney.idscan.cloud/',
        VUE_APP_ID_SCAN_FILE_BASE: '/id-scan/'
      };
      const config = {
        scanningToken: this.initGBGIdScan.scanningToken,
        investigationToken: this.initGBGIdScan.investigationToken,
        journeyDefinitionId: this.initGBGIdScan.journeyDefinitionId
      };

      try {
        this.IDScan = new IDScan(
          {
            assetsDirectory: env.VUE_APP_ID_SCAN_FILE_BASE,
            token: config.scanningToken,
            investigationToken: config.investigationToken,
            journeyDefinitionId: config.journeyDefinitionId,
            backendUrl: env.VUE_APP_ID_SCAN_BACKEND_URL,
            loadScript: this.$loadScript
          },
          this.journeyEvent,
          this.journeyError
        );
        await this.IDScan.start();
      } catch (e) {
        // eslint-disable-next-line
        console.error(e);
        this.journeyError();
      }
    },
    async resolveHighLevelResult() {
      if (this.highLevelResult) {
        return this.highLevelResult;
      }

      const fromJourney = this.IDScan?.eventJourney?.highLevelResult;
      if (fromJourney) {
        this.highLevelResult = fromJourney;
        return this.highLevelResult;
      }

      const retrieval = await this.IDScan.getRetrieval();
      this.highLevelResult = retrieval?.HighLevelResult;
      return this.highLevelResult;
    },
    async skip() {
      this.finished = true;
      this.errorCount = this.errorCount + 1;
      const { journeyId, journeyReference } = this.IDScan.results;

      await this.resolveHighLevelResult();

      const skippedValue = !isHighLevelResultPassed(this.highLevelResult);

      var dataParam = {
        idScan: {
          errorCount: this.errorCount,
          journeyId,
          journeyReference,
          skipped: skippedValue
        }
      };

      await this.updateIDScanData(dataParam);
      this.setIDScanDataStatus({
        isUserPrimaryApplicant: this.isUserPrimaryApplicant,
        data: dataParam
      });

      this.manualUpload = true;
    },
    async continue() {
      this.finished = true;
      const { journeyId, journeyReference } = this.IDScan.results;
      try {
        const retrieval = await this.IDScan.getRetrieval();
        this.highLevelResult = retrieval?.HighLevelResult;

        if (!isHighLevelResultPassed(this.highLevelResult)) {
          return this.journeyError();
        }

        const data = mapRetrieval(retrieval);

        var responseOnUpload = null;
        var responseUploadFile = null;

        const images = await this.IDScan.getDocumentImages();
        if (images['DrivingLicense-Front']) {
          responseOnUpload = await this.onUpload(images['DrivingLicense-Front']);
        } else throw new Error('DrivingLicense-Front not found!');

        if (responseOnUpload && responseOnUpload.data && responseOnUpload.data.getSignedUrl)
          responseUploadFile = await this.uploadFile(images['DrivingLicense-Front'], responseOnUpload.data.getSignedUrl.signedUrl);
        else throw new Error('Error upload files!');

        if (responseUploadFile) {
          this.fileUrls.push({
            uuid: responseOnUpload.data.getSignedUrl.uuid,
            fileName: responseOnUpload.data.getSignedUrl.fileName,
            mimeType: responseOnUpload.data.getSignedUrl.mimeType
          });
        } else throw new Error('Error on saving files!');

        if (images['DrivingLicense-Back']) {
          responseOnUpload = await this.onUpload(images['DrivingLicense-Back']);
        } else throw new Error('DrivingLicense-Back not found!');

        if (responseOnUpload && responseOnUpload.data && responseOnUpload.data.getSignedUrl)
          responseUploadFile = await this.uploadFile(images['DrivingLicense-Back'], responseOnUpload.data.getSignedUrl.signedUrl);
        else throw new Error('Error upload files!');

        if (responseUploadFile) {
          this.fileUrls.push({
            uuid: responseOnUpload.data.getSignedUrl.uuid,
            fileName: responseOnUpload.data.getSignedUrl.fileName,
            mimeType: responseOnUpload.data.getSignedUrl.mimeType
          });
        } else throw new Error('Error on saving files!');

        var dataParam = {
          firstName: data.firstName,
          lastName: data.lastName,
          middleName: data.middleName,
          dateOfBirth: data.dateOfBirth,
          fileUrls: this.fileUrls,
          addresses: [
            {
              streetNumber: data.streetNumber,
              address: data.address,
              address2: data.address2,
              suburb: data.suburb,
              state: data.state,
              postcode: data.postcode
            }
          ],
          addressFull: data.addressFull,
          licenseNumber: data.licenseNumber,
          cardNumber: data.cardNumber,
          licenseIssuedState: data.licenseIssuedState,
          licenseExpiry: data.licenseExpiry,
          passportNumber: data.passportNumber,
          passportIssuedBy: data.passportIssuedBy,
          idScan: {
            journeyId,
            journeyReference,
            skipped: false
          }
        };

        var response = await this.updateIDScanData(dataParam);

        if (response && response.data && response.data.createOrUpdateProperty && response.data.createOrUpdateProperty.status === '200') {
          this.setIDScanDataStatus({
            isUserPrimaryApplicant: this.isUserPrimaryApplicant,
            data: dataParam
          });
          this.onCancel('passed');
        }
      } catch (e) {
        // eslint-disable-next-line
        console.error(e);
        this.journeyError();
      }
    },
    onUpload: async function (file) {
      return await this.$apollo
        .mutate({
          mutation: MutateGetSignedUrl,
          variables: {
            file: file,
            shouldUpload: false
          }
        })
        .catch((error) => {
          this.error = true;
          if (error) {
            this.$euiNotifications.emit({
              notification: GLOBAL_ERROR
            });
          }
        });
    },
    uploadFile: async function (file, signedUrl) {
      return await fetch(signedUrl, {
        method: 'PUT',
        body: file,
        headers: {
          'Content-Type': file.type
        }
      }).catch((error) => {
        this.error = true;
        if (error) {
          this.$euiNotifications.emit({
            notification: GLOBAL_ERROR
          });
        }
      });
    },
    journeyEvent({ event, states, meta }, complete) {
      if (!this.initialised) {
        this.initialised = true;
      }

      if (complete && !this.finished) {
        this.continue();
      }
    },
    async journeyError() {
      this.error = true;
      if (this.displayingError) {
        return;
      }

      this.displayingError = true;

      this.$euiNotifications.emit({
        notification: GlobalDialog,
        noEscDismiss: true,
        persistent: true,
        body:
          '<div class="col-12" style="text-align: center">' +
          '<img src="/assets/images/alert.png" class="header-logo" />' +
          '</div>' +
          '<h4>Error occurred!</h4>' +
          '<span>We have not been able to capture your details.</span>',
        qCardStyle: {
          padding: '0',
          width: '400px',
          maxWidth: '80vw'
        },
        closeBtnStyle: {
          display: 'none'
        },
        btnOne: {
          label: 'OK',
          outline: true,
          color: 'primary',
          action: async () => {
            await this.skip();
          }
        }
      });
    },
    onSaved() {
      this.onCancel('passed');
    }
  },
  apollo: {
    initGBGIdScan: {
      query: QueryIDScan,
      debounce: 300,
      fetchPolicy: 'network-only',
      error: function () {
        this.$euiNotifications.emit({
          notification: GLOBAL_ERROR,
          action: () => {
            return this.$apollo.queries.initGBGIdScan.refresh();
          }
        });
      }
    }
  },
  components: {
    IDScan: () => import('./PlaceHolder'),
    QBtn,
    QDialog,
    QCard,
    QCardSection,
    QSpinner,
    UploadDialogBox
  }
};
</script>
